/**
 * JSON-LD (schema.org) structured data generators.
 *
 * All functions are pure — no side effects, no imports from site config.
 * Each accepts the data it needs and returns a typed JSON-LD object.
 */

// ── Input interfaces ────────────────────────────────────────────────────────

/** Flexible config shape (avoids coupling to `as const` literal type). */
export interface JsonLdSiteConfig {
  name: string;
  url: string;
  ogImage: string;
  city: {
    name: string;
    nameGenitive: string;
    region: string;
    coordinates: { lat: number; lng: number };
  };
  phone: string;
  email: string;
  address: string;
  workingHours: { start: number; end: number };
  links: { vk: string; instagram: string };
}

export interface ProductInput {
  id: number;
  name: string;
  description: string;
  image_url: string;
  price: number;
  is_available: boolean;
  calories?: number;
  protein?: number;
  fat?: number;
  carbohydrate?: number;
  weight?: number;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface BreadcrumbItem {
  name: string;
  href: string;
}

export interface DistrictInput {
  slug: string;
  name: string;
  deliveryTime: string;
}

// ── Schema types ────────────────────────────────────────────────────────────

interface JsonLdBase {
  '@context': 'https://schema.org';
  '@type': string;
  [key: string]: unknown;
}

// ── Generator functions ─────────────────────────────────────────────────────

/**
 * Restaurant schema with opening hours, cuisine, geo, and social links.
 * Requirements: 2.1, 2.7
 */
export function generateRestaurantSchema(config: JsonLdSiteConfig): JsonLdBase {
  const { start, end } = config.workingHours;

  return {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: config.name,
    url: config.url,
    telephone: config.phone,
    email: config.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: config.city.name,
      addressRegion: config.city.region,
      addressCountry: 'RU',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: config.city.coordinates.lat,
      longitude: config.city.coordinates.lng,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ],
        opens: `${String(start).padStart(2, '0')}:00`,
        closes: `${String(end).padStart(2, '0')}:00`,
      },
    ],
    servesCuisine: ['Японская кухня', 'Суши', 'Роллы'],
    priceRange: '₽₽',
    image: `${config.url}${config.ogImage}`,
    sameAs: [config.links.vk, config.links.instagram],
  };
}

/**
 * WebSite schema with SearchAction.
 * Requirements: 2.1
 */
export function generateWebSiteSchema(config: JsonLdSiteConfig): JsonLdBase {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: config.name,
    url: config.url,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${config.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Product schema with offers, optional nutrition and weight.
 * Requirements: 2.2
 */
export function generateProductSchema(
  product: ProductInput,
  config: JsonLdSiteConfig,
): JsonLdBase {
  const schema: JsonLdBase = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image_url,
    sku: String(product.id),
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: 'RUB',
      availability: product.is_available
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      url: config.url,
    },
  };

  // Optional nutrition info — only include if at least one value is present
  if (
    product.calories != null ||
    product.protein != null ||
    product.fat != null ||
    product.carbohydrate != null
  ) {
    schema.nutrition = {
      '@type': 'NutritionInformation',
      ...(product.calories != null && { calories: `${product.calories} cal` }),
      ...(product.protein != null && { proteinContent: `${product.protein} g` }),
      ...(product.fat != null && { fatContent: `${product.fat} g` }),
      ...(product.carbohydrate != null && { carbohydrateContent: `${product.carbohydrate} g` }),
    };
  }

  // Optional weight
  if (product.weight != null) {
    schema.weight = {
      '@type': 'QuantitativeValue',
      value: product.weight,
      unitCode: 'GRM',
    };
  }

  return schema;
}

/**
 * FAQPage schema with Question/Answer pairs.
 * Requirements: 2.3
 */
export function generateFAQSchema(items: FAQItem[]): JsonLdBase {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

/**
 * AggregateRating schema.
 * Requirements: 2.4
 */
export function generateAggregateRatingSchema(
  ratingValue: number,
  reviewCount: number,
): JsonLdBase {
  return {
    '@context': 'https://schema.org',
    '@type': 'AggregateRating',
    ratingValue,
    reviewCount,
    bestRating: 5,
    worstRating: 1,
  };
}

/**
 * BreadcrumbList schema.
 * Last item does NOT have the `item` property (current page).
 * Requirements: 2.6
 */
export function generateBreadcrumbSchema(items: BreadcrumbItem[]): JsonLdBase {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => {
      const isLast = index === items.length - 1;
      return {
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        ...(isLast ? {} : { item: item.href }),
      };
    }),
  };
}

/**
 * Menu schema with MenuSections grouped by category and MenuItems.
 * Generates schema.org Menu > MenuSection > MenuItem structure.
 */
export interface MenuItemInput {
  id: number;
  name: string;
  description: string;
  image_url: string;
  price: number;
  is_available: boolean;
}

export interface MenuSectionInput {
  name: string;
  items: MenuItemInput[];
}

export function generateMenuSchema(
  sections: MenuSectionInput[],
  config: JsonLdSiteConfig,
): JsonLdBase {
  return {
    '@context': 'https://schema.org',
    '@type': 'Menu',
    name: `Меню ${config.name}`,
    url: config.url,
    hasMenuSection: sections.map((section) => ({
      '@type': 'MenuSection',
      name: section.name,
      hasMenuItem: section.items
        .filter((item) => item.is_available)
        .map((item) => ({
          '@type': 'MenuItem',
          name: item.name,
          description: item.description,
          image: item.image_url,
          offers: {
            '@type': 'Offer',
            price: item.price,
            priceCurrency: 'RUB',
            availability: 'https://schema.org/InStock',
          },
        })),
    })),
  };
}

/**
 * LocalBusiness schema, optionally scoped to a specific district via areaServed.
 * Requirements: 2.5
 */
export function generateLocalBusinessSchema(
  config: JsonLdSiteConfig,
  district?: DistrictInput,
): JsonLdBase {
  const { start, end } = config.workingHours;

  const schema: JsonLdBase = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: config.name,
    url: config.url,
    telephone: config.phone,
    email: config.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: config.city.name,
      addressRegion: config.city.region,
      addressCountry: 'RU',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: config.city.coordinates.lat,
      longitude: config.city.coordinates.lng,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ],
        opens: `${String(start).padStart(2, '0')}:00`,
        closes: `${String(end).padStart(2, '0')}:00`,
      },
    ],
    image: `${config.url}${config.ogImage}`,
    sameAs: [config.links.vk, config.links.instagram],
  };

  if (district) {
    schema.areaServed = {
      '@type': 'Place',
      name: district.name,
    };
  }

  return schema;
}


/**
 * BlogPosting schema for individual blog articles.
 * Optimized for Google rich results.
 */
export interface BlogPostInput {
  title: string;
  excerpt: string;
  content?: string;
  image_url: string | null;
  published_at: string;
  slug: string;
  tags?: string[];
  meta_title?: string | null;
  meta_description?: string | null;
  reading_time?: number;
}

export function generateBlogPostSchema(
  post: BlogPostInput,
  config: JsonLdSiteConfig,
): JsonLdBase {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.meta_title || post.title,
    description: post.meta_description || post.excerpt,
    image: post.image_url || `${config.url}${config.ogImage}`,
    datePublished: post.published_at,
    dateModified: post.published_at,
    author: {
      '@type': 'Organization',
      name: config.name,
      url: config.url,
    },
    publisher: {
      '@type': 'Organization',
      name: config.name,
      logo: {
        '@type': 'ImageObject',
        url: `${config.url}/icon.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${config.url}/blog/${post.slug}`,
    },
    ...(post.tags && post.tags.length > 0 && { keywords: post.tags.join(', ') }),
    ...(post.reading_time && {
      timeRequired: `PT${post.reading_time}M`,
    }),
  };
}

/**
 * Blog listing page schema.
 */
export function generateBlogListSchema(config: JsonLdSiteConfig): JsonLdBase {
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: `Блог ${config.name}`,
    description: `Статьи о японской кухне, рецепты, советы по выбору роллов и суши`,
    url: `${config.url}/blog`,
    publisher: {
      '@type': 'Organization',
      name: config.name,
      url: config.url,
    },
  };
}
